//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';

import Text from '@components/atoms/text/Text';
import TimeZoneId from '@models/TimeZoneId';
import { Mixins } from '@theme/mixins';
import { momentFromDate } from '@utils/moment';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  timestamp: number | Date;
  timezone: TimeZoneId | 'user';
  prefixText?: string;
  customFormat?: string;
  useRelativeFormat: boolean;
  height?: React.ComponentProps<typeof Text>['height'];
  ellipsize?: React.ComponentProps<typeof Text>['ellipsize'];
}

const BaseTimestamp = ({
  className,
  customFormat,
  ellipsize,
  height,
  prefixText,
  timestamp,
  timezone,
  useRelativeFormat,
}: Props) => {
  // TODO: Look into dropping this selector. It isn't free and is used hundreds of
  // times on pages with many timetsamps.
  const userTimezone = useSelector((state) => state.session.timeZone.id);
  const tz = timezone === 'user' ? userTimezone : timezone;
  const momentTimestamp = momentFromDate(timestamp, tz);
  const date = useRelativeFormat
    ? moment(timestamp).fromNow()
    : momentTimestamp.isValid() &&
      momentTimestamp.format(customFormat || 'MMM D, YYYY, h:mm a z');

  return date ? (
    <Text
      className={className}
      ellipsize={ellipsize}
      height={height}
      mixin={Mixins.Text.Metatag}
      title={date}
    >
      {prefixText ? `${prefixText} ${date}` : date}
    </Text>
  ) : null;
};

/**
 * The Timestamp atom renders a human readable date and time
 * based on a timestamp in milliseconds with an optional prefix
 * string. This string is useful if the timestamp requires some
 * additional context. If necessary, you can provide your own
 * format string or set the boolean useRelativeFormat to true.
 *
 * @example
 * // Returns PUBLISHED ON NOV 1, 2019, 2:01 PM
 * <Timestamp timestamp={ 1572631261000 } prefixText='Published on' />
 */
export const Timestamp = (props: Omit<Props, 'useRelativeFormat'>) => {
  return <BaseTimestamp useRelativeFormat={false} {...props} />;
};

/**
 * The RelativeTimestamp atom renders a human readable relative time,
 * such as N DAYS AGO or N HOURS AGO.
 */
export const RelativeTimestamp = (
  props: Omit<Props, 'useRelativeFormat' | 'timezone'>,
) => {
  return (
    <BaseTimestamp
      useRelativeFormat={true}
      // TimeZone is irrelevant when using relative time, pass in UTC to appease our Prop Types
      timezone={TimeZoneId.Utc}
      {...props}
    />
  );
};

export default Timestamp;
